import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Leaf, LogOut, Loader2, Trash2, Plus, Megaphone, Package, ImageIcon, ExternalLink, Ticket, KeyRound, GripVertical } from "lucide-react";
import { adminApi, clearToken, getToken, imageUrl } from "@/api";

const TABS = [
  { key: "announcement", label: "Announcement", icon: Megaphone },
  { key: "products", label: "Products", icon: Package },
  { key: "ads", label: "Ad Banners", icon: ImageIcon },
  { key: "coupons", label: "Coupons", icon: Ticket },
  { key: "password", label: "Password", icon: KeyRound },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-[var(--ink)]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
const inputCls = "w-full h-11 rounded-xl border border-[var(--border)] bg-white px-3.5 outline-none focus:border-[var(--forest)] transition-colors";

const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
    className={`relative h-7 w-12 rounded-full transition-colors ${checked ? "bg-[var(--forest)]" : "bg-gray-300"}`}>
    <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${checked ? "left-6" : "left-1"}`} />
  </button>
);

// ---------- Announcement ----------
const AnnouncementTab = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.get("/admin/announcement").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-[var(--forest)]" />;
  const save = async () => {
    setSaving(true);
    try { await adminApi.put("/admin/announcement", data); toast.success("Announcement saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl space-y-5">
      <div className="flex items-center justify-between rounded-xl border border-[var(--border)] bg-white p-4">
        <div><p className="font-medium">Show announcement bar</p><p className="text-sm text-[var(--muted)]">Top strip on the website</p></div>
        <Toggle checked={data.enabled} onChange={(v) => setData({ ...data, enabled: v })} testid="ann-enabled" />
      </div>
      <Field label="Announcement text">
        <input data-testid="ann-text" className={inputCls} value={data.text} onChange={(e) => setData({ ...data, text: e.target.value })} placeholder="e.g. Diwali offer: 10% off on 15KG" />
      </Field>
      <Field label="Link (optional)">
        <input data-testid="ann-link" className={inputCls} value={data.link || ""} onChange={(e) => setData({ ...data, link: e.target.value })} placeholder="https://..." />
      </Field>
      <button data-testid="ann-save" onClick={save} disabled={saving}
        className="h-11 px-6 rounded-full bg-[var(--forest)] text-white font-bold uppercase tracking-widest text-sm hover:bg-[var(--forest-2)] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
      </button>
    </div>
  );
};

// ---------- Products ----------
const ProductCard = ({ product }) => {
  const [p, setP] = useState(product);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/admin/products/${p.key}`, {
        size: p.size, price: p.price, desc: p.desc, offer_text: p.offer_text || "",
        popular: !!p.popular, enabled: p.enabled !== false, order: p.order || 0,
      });
      toast.success(`${p.size} saved`);
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-[var(--border)] bg-white p-5 space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-serif-display text-2xl text-[var(--forest)]">{p.size}</span>
        <Toggle checked={p.enabled !== false} onChange={(v) => setP({ ...p, enabled: v })} testid={`prod-${p.key}-enabled`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Size"><input className={inputCls} value={p.size} onChange={(e) => setP({ ...p, size: e.target.value })} data-testid={`prod-${p.key}-size`} /></Field>
        <Field label="Price (₹)"><input className={inputCls} value={p.price} onChange={(e) => setP({ ...p, price: e.target.value })} data-testid={`prod-${p.key}-price`} /></Field>
      </div>
      <Field label="Description"><input className={inputCls} value={p.desc} onChange={(e) => setP({ ...p, desc: e.target.value })} data-testid={`prod-${p.key}-desc`} /></Field>
      <Field label="Offer ribbon (optional, e.g. 20% OFF)"><input className={inputCls} value={p.offer_text || ""} onChange={(e) => setP({ ...p, offer_text: e.target.value })} data-testid={`prod-${p.key}-offer`} /></Field>
      <div className="flex items-center justify-between pt-1">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!p.popular} onChange={(e) => setP({ ...p, popular: e.target.checked })} data-testid={`prod-${p.key}-popular`} /> Most Popular</label>
        <button data-testid={`prod-${p.key}-save`} onClick={save} disabled={saving}
          className="h-10 px-5 rounded-full bg-[var(--forest)] text-white font-bold uppercase tracking-widest text-xs hover:bg-[var(--forest-2)] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
        </button>
      </div>
    </div>
  );
};
const ProductsTab = () => {
  const [products, setProducts] = useState(null);
  useEffect(() => { adminApi.get("/admin/products").then((r) => setProducts(r.data)); }, []);
  if (!products) return <Loader2 className="h-5 w-5 animate-spin text-[var(--forest)]" />;
  return <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{products.map((p) => <ProductCard key={p.key} product={p} />)}</div>;
};

// ---------- Ads ----------
const AdsTab = () => {
  const [ads, setAds] = useState(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const load = () => adminApi.get("/admin/ads").then((r) => setAds(r.data));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) { toast.error("Please choose an image"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("title", title); fd.append("link", link);
    fd.append("enabled", "true"); fd.append("order", String((ads?.length || 0) + 1));
    try {
      await adminApi.post("/admin/ads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ad banner added");
      setFile(null); setTitle(""); setLink("");
      document.getElementById("ad-file-input").value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };
  const toggle = async (ad) => { await adminApi.put(`/admin/ads/${ad.id}`, { title: ad.title, link: ad.link, enabled: !ad.enabled, order: ad.order }); load(); };
  const remove = async (ad) => { await adminApi.delete(`/admin/ads/${ad.id}`); toast.success("Ad deleted"); load(); };
  const dragIndex = useRef(null);
  const onDrop = async (i) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === i) return;
    const arr = [...ads];
    const [moved] = arr.splice(from, 1);
    arr.splice(i, 0, moved);
    setAds(arr);
    await adminApi.post("/admin/ads/reorder", { ids: arr.map((a) => a.id) });
    toast.success("Order updated");
  };

  if (!ads) return <Loader2 className="h-5 w-5 animate-spin text-[var(--forest)]" />;
  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-[var(--border)] bg-white p-5 max-w-xl space-y-4">
        <h3 className="font-serif-display text-2xl text-[var(--forest)]">Add new ad banner</h3>
        <Field label="Banner image (jpg / png / webp)">
          <input id="ad-file-input" data-testid="ad-file" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])}
            className="w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[var(--forest)] file:text-white file:px-4 file:py-2 file:font-medium" />
        </Field>
        <Field label="Title (optional)"><input data-testid="ad-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Link (optional)"><input data-testid="ad-link" className={inputCls} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://..." /></Field>
        <button data-testid="ad-upload" onClick={upload} disabled={uploading}
          className="h-11 px-6 rounded-full bg-[var(--forest)] text-white font-bold uppercase tracking-widest text-sm hover:bg-[var(--forest-2)] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Banner
        </button>
      </div>

      <div>
        <p className="text-sm text-[var(--muted)] mb-3 flex items-center gap-1.5">Drag the <GripVertical className="h-4 w-4" /> handle to reorder how banners appear on the site.</p>
        <div className="space-y-4 max-w-3xl">
          {ads.length === 0 && <p className="text-[var(--muted)]">No ad banners yet.</p>}
          {ads.map((ad, i) => (
            <div key={ad.id} data-testid={`admin-ad-${ad.id}`} draggable
              onDragStart={() => (dragIndex.current = i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className="flex items-center gap-3 sm:gap-4 rounded-2xl border border-[var(--border)] bg-white p-3">
              <span className="cursor-grab active:cursor-grabbing text-[var(--muted)] shrink-0" data-testid={`ad-${ad.id}-drag`}><GripVertical className="h-5 w-5" /></span>
              <img src={imageUrl(ad.image_url)} alt={ad.title} className="w-24 sm:w-28 h-14 sm:h-16 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{ad.title || "Untitled"}</p>
                {ad.link && <a href={ad.link} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--forest-2)] inline-flex items-center gap-1 max-w-full truncate"><ExternalLink className="h-3 w-3 shrink-0" /> {ad.link}</a>}
              </div>
              <Toggle checked={ad.enabled} onChange={() => toggle(ad)} testid={`ad-${ad.id}-toggle`} />
              <button data-testid={`ad-${ad.id}-delete`} onClick={() => remove(ad)} className="h-9 w-9 shrink-0 rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition-colors inline-flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- Coupons ----------
const CouponsTab = () => {
  const [coupons, setCoupons] = useState(null);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => adminApi.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!code.trim() || !discount.trim()) { toast.error("Code aur discount dono zaroori hai"); return; }
    setSaving(true);
    try {
      await adminApi.post("/admin/coupons", { code, discount, description, enabled: true, order: (coupons?.length || 0) });
      toast.success("Coupon added");
      setCode(""); setDiscount(""); setDescription(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  const toggle = async (c) => { await adminApi.put(`/admin/coupons/${c.id}`, { code: c.code, discount: c.discount, description: c.description, enabled: !c.enabled, order: c.order }); load(); };
  const remove = async (c) => { await adminApi.delete(`/admin/coupons/${c.id}`); toast.success("Coupon deleted"); load(); };

  if (!coupons) return <Loader2 className="h-5 w-5 animate-spin text-[var(--forest)]" />;
  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-[var(--border)] bg-white p-5 max-w-xl space-y-4">
        <h3 className="font-serif-display text-2xl text-[var(--forest)]">Add promo code</h3>
        <p className="text-sm text-[var(--muted)]">Customers ye code WhatsApp par bhejenge extra discount ke liye.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code"><input data-testid="coupon-code" className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. GREEN10" /></Field>
          <Field label="Discount"><input data-testid="coupon-discount" className={inputCls} value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="e.g. 10% OFF" /></Field>
        </div>
        <Field label="Description (optional)"><input data-testid="coupon-desc" className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. On 15KG orders" /></Field>
        <button data-testid="coupon-add" onClick={add} disabled={saving}
          className="h-11 px-6 rounded-full bg-[var(--forest)] text-white font-bold uppercase tracking-widest text-sm hover:bg-[var(--forest-2)] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Coupon
        </button>
      </div>
      <div className="space-y-3 max-w-2xl">
        {coupons.length === 0 && <p className="text-[var(--muted)]">No coupons yet.</p>}
        {coupons.map((c) => (
          <div key={c.id} data-testid={`admin-coupon-${c.id}`} className="flex items-center gap-4 rounded-2xl border border-[var(--border)] bg-white p-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-accent font-bold tracking-wider text-[var(--forest)] bg-[var(--beige)] px-3 py-1 rounded-lg">{c.code}</span>
                <span className="text-sm font-bold text-red-600">{c.discount}</span>
              </div>
              {c.description && <p className="text-sm text-[var(--muted)] mt-1 truncate">{c.description}</p>}
            </div>
            <Toggle checked={c.enabled} onChange={() => toggle(c)} testid={`coupon-${c.id}-toggle`} />
            <button data-testid={`coupon-${c.id}-delete`} onClick={() => remove(c)} className="h-9 w-9 shrink-0 rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition-colors inline-flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Password ----------
const PasswordTab = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (next.length < 6) { toast.error("New password kam se kam 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords match nahi kar rahe"); return; }
    setSaving(true);
    try {
      await adminApi.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-md space-y-4">
      <div className="rounded-2xl border border-[var(--border)] bg-white p-5 space-y-4">
        <h3 className="font-serif-display text-2xl text-[var(--forest)]">Change login password</h3>
        <Field label="Current password"><input data-testid="pw-current" type="password" className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>
        <Field label="New password"><input data-testid="pw-new" type="password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} /></Field>
        <Field label="Confirm new password"><input data-testid="pw-confirm" type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
        <button data-testid="pw-save" onClick={save} disabled={saving}
          className="h-11 px-6 rounded-full bg-[var(--forest)] text-white font-bold uppercase tracking-widest text-sm hover:bg-[var(--forest-2)] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Update Password
        </button>
      </div>
    </div>
  );
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("announcement");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    adminApi.get("/auth/me").then(() => setReady(true)).catch(() => { clearToken(); navigate("/admin/login"); });
  }, [navigate]);

  const logout = () => { clearToken(); navigate("/admin/login"); };
  if (!ready) return <div className="min-h-screen flex items-center justify-center bg-[var(--cream)]"><Loader2 className="h-6 w-6 animate-spin text-[var(--forest)]" /></div>;

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <Toaster position="top-right" richColors />
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5"><img src="/logo.png" alt="Earth Nest Organics logo" className="h-8 w-8 object-contain" /><span className="font-accent font-extrabold tracking-tight text-[var(--forest)]">EARTH NEST · ADMIN</span></div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-[var(--forest-2)] hover:underline hidden sm:inline">View site</a>
            <button data-testid="logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[var(--border)] text-sm font-medium hover:bg-[var(--beige)] transition-colors"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif-display text-4xl text-[var(--forest)] mb-6">Dashboard</h1>
        <div className="flex gap-2 mb-8 flex-wrap">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-medium transition-colors ${tab === t.key ? "bg-[var(--forest)] text-white" : "bg-white border border-[var(--border)] text-[var(--ink)] hover:bg-[var(--beige)]"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "announcement" && <AnnouncementTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "ads" && <AdsTab />}
        {tab === "coupons" && <CouponsTab />}
        {tab === "password" && <PasswordTab />}
      </div>
    </div>
  );
};
